<script>
export default {
  name: "DidoMillesimeTableModalDownload",
};
</script>

<script setup>
import { ref, watch, computed } from "vue";
import { showNotification } from "../../../../utils";

// define Props
const props = defineProps({
  modelValue: { type: Boolean, required: false, default: false },
  tableWrapperEl: { type: Object, required: true },
  downloadLinks: { type: Object, required: true },
});

// define Emits
const emit = defineEmits(["update:model-value"]);

// define reactive data
const opened = ref(props.modelValue);

// define watcher when modelValue change
watch(
  () => [props.modelValue],
  () => (opened.value = props.modelValue),
);

// define computed data for geoFields
const geoFields = computed(() => Object.keys(props.downloadLinks.geojson));

// method: copy a link to clipboard
const copyDownloadLinkToClipboard = (link) => {
  const el = document.createElement("textarea");
  el.value = link;
  props.tableWrapperEl.appendChild(el);
  el.select();
  document.execCommand("copy");
  props.tableWrapperEl.removeChild(el);
  showNotification({
    type: "info",
    message: "Le lien a été copié dans le presse-papier",
    position: "top",
    icon: "mdi-information",
    timeout: 2500,
  });
};
</script>

<template>
  <QDialog
    v-model="opened"
    @update:model-value="emit('update:model-value', opened)"
  >
    <div class="bg-white text-secondary">
      <QBar class="bg-secondary text-white">
        <QSpace />
        <QBtn v-close-popup dense flat icon="mdi-window-close" />
      </QBar>
      <QList bordered dense>
        <QItemLabel header>
          Liens pour récupérer le tableau dans différents formats :
        </QItemLabel>
        <QSeparator spaced />
        <!-- csv format -->
        <QItem>
          <QItemSection avatar top>
            <QIcon name="mdi-file-delimited" size="sm" color="secondary" />
          </QItemSection>
          <QItemSection top class="text-black text-small">
            L'ensemble du tableau au format csv
          </QItemSection>
          <QItemSection side top>
            <div class="q-gutter-xs">
              <QBtn
                :href="props.downloadLinks.csv"
                target="_blank"
                size="sm"
                flat
                dense
                color="primary"
                icon="mdi-download"
                title="Télécharger l'ensemble du tableau au format csv"
              />
              <QBtn
                size="sm"
                flat
                dense
                round
                icon="mdi-clipboard-outline"
                title="Copier le lien de téléchargement de l'ensemble du tableau au format csv dans le presse-papier"
                color="primary"
                @click="copyDownloadLinkToClipboard(props.downloadLinks.csv)"
              />
            </div>
          </QItemSection>
        </QItem>
        <!-- xlsx format -->
        <QItem>
          <QItemSection avatar top>
            <QIcon name="mdi-microsoft-excel" size="sm" color="secondary" />
          </QItemSection>
          <QItemSection top class="text-black text-small">
            L'ensemble du tableau au format xlsx
          </QItemSection>
          <QItemSection side top>
            <div class="q-gutter-xs">
              <QBtn
                :href="props.downloadLinks.xlsx"
                target="_blank"
                size="sm"
                flat
                dense
                color="primary"
                icon="mdi-download"
                title="Télécharger l'ensemble du tableau au format xlsx"
              />
              <QBtn
                size="sm"
                flat
                dense
                round
                icon="mdi-clipboard-outline"
                title="Copier le lien de téléchargement de l'ensemble du tableau au format xlsx dans le presse-papier"
                color="primary"
                @click="copyDownloadLinkToClipboard(props.downloadLinks.xlsx)"
              />
            </div>
          </QItemSection>
        </QItem>
        <!-- json format -->
        <QItem>
          <QItemSection avatar top>
            <QIcon name="mdi-code-json" size="sm" color="secondary" />
          </QItemSection>
          <QItemSection top class="text-black text-small">
            L'ensemble du tableau au format json
          </QItemSection>
          <QItemSection side top>
            <div class="q-gutter-xs">
              <QBtn
                :href="props.downloadLinks.json"
                target="_blank"
                size="sm"
                flat
                dense
                color="primary"
                icon="mdi-download"
                title="Télécharger l'ensemble du tableau au format json"
              />
              <QBtn
                size="sm"
                flat
                dense
                round
                icon="mdi-clipboard-outline"
                title="Copier le lien de téléchargement de l'ensemble du tableau au format json dans le presse-papier"
                color="primary"
                @click="copyDownloadLinkToClipboard(props.downloadLinks.json)"
              />
            </div>
          </QItemSection>
        </QItem>
        <!-- spatial format -->
        <template v-if="geoFields">
          <!-- one for each geofield -->
          <template
            v-for="(geoField, index) in geoFields"
            :key="`geofield-${index}`"
          >
            <QSeparator spaced />
            <!-- geojson format -->
            <QItem>
              <QItemSection avatar top>
                <QIcon name="mdi-code-json" size="sm" color="secondary" />
              </QItemSection>
              <QItemSection top class="text-black text-small">
                L'ensemble du tableau au format geojson (ref {{ geoField }})
              </QItemSection>
              <QItemSection side top>
                <div class="q-gutter-xs">
                  <QBtn
                    :href="props.downloadLinks.geojson[geoField]"
                    target="_blank"
                    size="sm"
                    flat
                    dense
                    color="primary"
                    icon="mdi-download"
                    title="Télécharger l'ensemble du tableau au format geojson"
                  />
                  <QBtn
                    size="sm"
                    flat
                    dense
                    round
                    icon="mdi-clipboard-outline"
                    title="Copier le lien de téléchargement de l'ensemble du tableau au format geojson dans le presse-papier"
                    color="primary"
                    @click="
                      copyDownloadLinkToClipboard(
                        props.downloadLinks.geojson[geoField],
                      )
                    "
                  />
                </div>
              </QItemSection>
            </QItem>
          </template>
        </template>
      </QList>
    </div>
  </QDialog>
</template>
